import { useEffect, useRef, useState } from "react";
import { toLocalISODate } from "@/lib/utils";

type Preset = "7d" | "30d" | "fy" | "all";

// Local calendar date (not UTC) — the default range's `to` bound must be the
// user's today, or early-morning IST users get a range ending yesterday that
// hides the row they just saved. See toLocalISODate.
const formatDate = toLocalISODate;

/** Returns a list of Indian FY options, current + past N years. */
function getFYOptions(count = 5): { label: string; from: string; to: string }[] {
  const today = new Date();
  const currentFYStart =
    today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const options: { label: string; from: string; to: string }[] = [];
  for (let i = 0; i < count; i++) {
    const startYear = currentFYStart - i;
    const endYear = startYear + 1;
    const from = `${startYear}-04-01`;
    // For current FY, cap 'to' at today; for past FYs, use March 31
    const to = i === 0 ? formatDate(today) : `${endYear}-03-31`;
    options.push({
      label: `FY ${String(startYear).slice(2)}-${String(endYear).slice(2)}`,
      from,
      to,
    });
  }
  return options;
}

function getPresetRange(preset: Preset): { from: string; to: string } {
  const today = new Date();
  const to = formatDate(today);
  switch (preset) {
    case "7d": {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: formatDate(d), to };
    }
    case "30d": {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: formatDate(d), to };
    }
    case "fy": {
      // Indian financial year: April 1 – March 31 (current)
      const year =
        today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      return { from: `${year}-04-01`, to };
    }
    case "all":
      return { from: "", to: "" };
  }
}

export function getDefaultDateRange() {
  // Default to the current financial year, not the last 7 days. Farm logging is
  // often sparse/stale, so a 7-day window frequently renders "No X found" even
  // when there IS current-year data (sales, bills, logs) — which reads as "no
  // data ever." FY surfaces the current year's data without dumping all history.
  return getPresetRange("fy");
}

interface DateRangeFilterProps {
  onApply: (from: string, to: string) => void;
}

const fyOptions = getFYOptions(5);

// A typed date applies on its own, like the preset buttons, once it is a whole
// date. While a year is being typed the native input briefly reports values
// like 0002-10-07, so anything before 2000 counts as unfinished, and the short
// delay stops the list reloading on every keystroke.
const APPLY_DELAY_MS = 500;
const isCompleteDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && v >= "2000-01-01";

const dateInputClass =
  "h-8 w-[130px] rounded-lg border bg-transparent px-2 text-xs text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90";

export default function DateRangeFilter({ onApply }: DateRangeFilterProps) {
  const defaults = getDefaultDateRange();
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [activePreset, setActivePreset] = useState<Preset | null>(null);
  const [activeFY, setActiveFY] = useState<string | null>(getFYOptions()[0].label);
  const [showFYMenu, setShowFYMenu] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const isCustom = activePreset === null && activeFY === null;
  // "from" after "to" matches nothing; say so on the inputs rather than load an empty list.
  const isBackwards = isCompleteDate(from) && isCompleteDate(to) && from > to;

  const cancelPending = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const scheduleApply = (nextFrom: string, nextTo: string) => {
    cancelPending();
    // A cleared box is a real choice (no bound on that side); a half-typed one is not.
    const ok = (v: string) => v === "" || isCompleteDate(v);
    if (!ok(nextFrom) || !ok(nextTo)) return;
    if (nextFrom && nextTo && nextFrom > nextTo) return;
    timer.current = setTimeout(() => onApply(nextFrom, nextTo), APPLY_DELAY_MS);
  };

  const handlePreset = (preset: Preset) => {
    if (preset === "fy") {
      // Toggle FY dropdown instead of immediately applying
      setShowFYMenu((v) => !v);
      return;
    }
    setShowFYMenu(false);
    cancelPending();
    const range = getPresetRange(preset);
    setFrom(range.from);
    setTo(range.to);
    setActivePreset(preset);
    setActiveFY(null);
    onApply(range.from, range.to);
  };

  const handleFYSelect = (fy: (typeof fyOptions)[0]) => {
    setFrom(fy.from);
    setTo(fy.to);
    setActivePreset(null);
    setActiveFY(fy.label);
    setShowFYMenu(false);
    cancelPending();
    onApply(fy.from, fy.to);
  };

  const handleFromChange = (value: string) => {
    setFrom(value);
    setActivePreset(null);
    setActiveFY(null);
    scheduleApply(value, to);
  };

  const handleToChange = (value: string) => {
    setTo(value);
    setActivePreset(null);
    setActiveFY(null);
    scheduleApply(from, value);
  };

  const quickPresets: { key: Preset; label: string }[] = [
    { key: "7d", label: "7 Days" },
    { key: "30d", label: "30 Days" },
  ];

  // Border color for date inputs: highlight when custom range is applied
  const dateInputBorder = isBackwards
    ? "border-error-500 dark:border-error-500"
    : isCustom
    ? "border-brand-500 dark:border-brand-500"
    : "border-gray-300 dark:border-gray-700";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
        {quickPresets.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => handlePreset(p.key)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              activePreset === p.key
                ? "bg-brand-500 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white/80"
            }`}
          >
            {p.label}
          </button>
        ))}

        {/* FY dropdown trigger */}
        <div className="relative">
          <button
            type="button"
            onClick={() => handlePreset("fy")}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors flex items-center gap-0.5 ${
              activeFY
                ? "bg-brand-500 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white/80"
            }`}
          >
            {activeFY || "FY"}
            <svg className="w-3 h-3 ml-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          {showFYMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowFYMenu(false)} />
              <div className="absolute left-0 top-full mt-1 z-20 w-32 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                {fyOptions.map((fy) => (
                  <button
                    key={fy.label}
                    type="button"
                    onClick={() => handleFYSelect(fy)}
                    className={`block w-full px-3 py-1.5 text-left text-xs transition-colors ${
                      activeFY === fy.label
                        ? "bg-brand-50 text-brand-600 font-medium dark:bg-brand-500/10 dark:text-brand-400"
                        : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                  >
                    {fy.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => handlePreset("all")}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
            activePreset === "all"
              ? "bg-brand-500 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white/80"
          }`}
        >
          All
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <input
          type="date"
          value={from}
          onChange={(e) => handleFromChange(e.target.value)}
          onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
          className={`${dateInputClass} ${dateInputBorder}`}
        />
        <span className="text-xs text-gray-400">&ndash;</span>
        <input
          type="date"
          value={to}
          onChange={(e) => handleToChange(e.target.value)}
          onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
          className={`${dateInputClass} ${dateInputBorder}`}
        />
      </div>

      {isBackwards && (
        <span className="text-[10px] font-medium text-error-600 dark:text-error-500">
          Start date is after end date
        </span>
      )}
      {isCustom && (
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
          Custom range
        </span>
      )}
    </div>
  );
}
